import type { GenericFieldConfig, GenericFormType } from '@neo/blocks/forms/types'
import { v4 } from 'uuid'
import { useAccountNumbersQuery } from 'v2/pages/settings/phone-numbers/queries'
import type { FileMimeType, RFileUploadResponse } from 'v2/types/api/auth'
import { ConvChannels } from 'v2/types/api/conversations'
import { type EventCallback, EventCallbacks } from '../components/event-callbacks'
import { MediaUpload } from '../components/media-upload/media-upload'
import { NodevarList } from '../components/nodevar-list'
import { v2validations } from '../config/validation.config'
import type { SendMessageNodeData, SidebarComponentProps } from '../types'
import { GenericSidebarComponent } from './sidebar.base'

type FormValues = SendMessageNodeData['config']

// Node-level event callbacks for SMS (legacy PHLO message-status events). SER-4772.
const sendMessageEventTypes = [
  { value: 'queued', label: 'Queued' },
  { value: 'sent', label: 'Sent' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'failed', label: 'Failed' },
  { value: 'undelivered', label: 'Undelivered' },
]

const fields = (
  nodeVars: string[],
  fieldsWithDynamicNodeVars: Record<string, string[]>,
): GenericFieldConfig<FormValues> => {
  const validations = v2validations({ nodeVars }).send_message
  return [
    {
      name: 'name',
      label: 'Name',
      zodValidation: validations.shape.name,
      componentType: 'input',
      inputProps: {
        type: 'text',
      },
    },
    {
      name: 'from',
      label: 'Send message from',
      zodValidation: validations.shape.from,
      componentType: 'input',
      inputProps: {
        endIcon: (
          <NodevarList
            nodevars={fieldsWithDynamicNodeVars.from}
            className="pr-2"
            inputElementSelector="[name=from]"
          />
        ),
      },
    },
    {
      name: 'to',
      label: 'To',
      zodValidation: validations.shape.to,
      componentType: 'input',
      inputProps: {
        endIcon: (
          <NodevarList nodevars={nodeVars} className="pr-2" inputElementSelector="[name=to]" />
        ),
      },
    },
    {
      name: 'text',
      label: 'Text',
      zodValidation: validations.shape.text,
      componentType: 'textarea',
      inputProps: {
        placeholder: 'Enter text here',
        endIcon: (
          <NodevarList
            nodevars={nodeVars}
            className="pr-2"
            inputElementSelector="[name=text]"
            sideOffset={40}
          />
        ),
      },
    },
    {
      name: 'message_media',
      type: 'custom',
      label: 'Upload media (optional)',
      zodValidation: validations.shape.message_media,
      eventDataExtractor: (data: Record<string, unknown>) => data,
      component: ({
        form,
        onChange,
      }: {
        form: GenericFormType<SendMessageNodeData['config']>
        onChange: (value: Record<string, unknown>[]) => void
      }) => {
        const onUrlChange = (uploadedFile: RFileUploadResponse) => {
          const mediaFiles = {
            media_type: uploadedFile.file_type,
            name: uploadedFile.file_name,
            url: uploadedFile.file_url,
            size: uploadedFile.file_size,
            file_id: uploadedFile.file_id,
          }
          onChange([...form.state.values.message_media, mediaFiles])
        }

        const onRemove = (fileToRemove: RFileUploadResponse) => {
          onChange(
            form.state.values.message_media.filter((file) => file.url !== fileToRemove.file_url),
          )
        }
        let initialFiles: RFileUploadResponse[] = []
        if (form.state.values.message_media) {
          initialFiles = form.state.values.message_media.map((file) => ({
            file_type: file.media_type as FileMimeType,
            file_name: file.name || '',
            file_url: file.url || '',
            file_size: file.size || 0,
            file_id: file.file_id || v4(),
          }))
        }
        return (
          <MediaUpload
            onURLChange={onUrlChange}
            onRemove={onRemove}
            label="Upload media (optional)"
            initialFiles={initialFiles}
            nodeVars={nodeVars}
          />
        )
      },
    },
    {
      name: 'wait_for_response',
      label: 'Wait for response',
      zodValidation: validations.shape.wait_for_response,
      componentType: 'checkbox',
    },
    {
      name: 'reply_timeout',
      label: 'Reply timeout',
      zodValidation: validations.shape.reply_timeout,
      visible: (values) => values.wait_for_response === true,
      onChangeListenTo: ['wait_for_response'],
      componentType: 'inputNumber',
      inputProps: {
        type: 'number',
        min: 60,
        max: 259200,
        defaultValue: 3600,
        endIcon: <span className="text-tertiary text-p-400 mr-7">Seconds</span>,
      },
    },
    {
      name: 'events',
      type: 'slot',
      element: ({ form }) => (
        <div className="col-span-2">
          <form.Field name="events">
            {(field) => (
              <EventCallbacks
                events={(field.state.value as EventCallback[]) ?? []}
                onChange={(events) => field.handleChange(events)}
                eventTypes={sendMessageEventTypes}
                nodeVars={nodeVars}
                className="mt-4"
              />
            )}
          </form.Field>
        </div>
      ),
    },
  ]
}

// NODE_GEN: Update this code, do not commit directly
// Remove this comment after updating the code

export const SendMessageSidebarComponent = (props: SidebarComponentProps<SendMessageNodeData>) => {
  const { nodeVars } = props
  const { data: accountNumbers } = useAccountNumbersQuery('simple', ConvChannels.MESSAGE)
  const senderIds = accountNumbers?.map((number) => number.number)
  const fieldsWithDynamicNodeVars = {
    from: [...(senderIds?.flat() || []), ...nodeVars],
  }
  const formFields = fields(nodeVars, fieldsWithDynamicNodeVars)

  return <GenericSidebarComponent fields={formFields} {...props} />
}
